/**
 * Base API client configuration
 * Provides a configured axios instance with interceptors and error handling
 */

import { API_TIMEOUT } from '@/config/constants';
import env from '@/config/env';
import { logger } from '@/utils/logger';
import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

// Define global constants for API configuration
const API_BASE_URL = env.backendUrl;
const API_DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
};

// Store the Auth0 token-fetching function (not the token itself)
let getAccessTokenSilently: (() => Promise<string>) | null = null;

// Flag to prevent multiple simultaneous token refresh attempts
let isRefreshingToken = false;
let tokenPromise: Promise<string> | null = null;

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: API_DEFAULT_HEADERS,
  withCredentials: true, // Include cookies in requests
});

/**
 * Configure the API client with Auth0's token-fetching function.
 * This should be called once during app initialization after Auth0 is ready.
 */
export const configureApiClient = (tokenFetcher: () => Promise<string>): void => {
  getAccessTokenSilently = tokenFetcher;
  logger.debug('API client configured with Auth0 token fetcher');
};

/**
 * Request interceptor: Inject fresh access token before each request
 */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Skip token injection if no token fetcher configured (shouldn't happen in normal flow)
    if (!getAccessTokenSilently) {
      logger.warn('API request made before configureApiClient was called');
      return config;
    }

    try {
      // Get fresh token from Auth0 (Auth0 SDK handles caching internally)
      const token = await getAccessTokenSilently();

      // Inject token into request headers
      if (config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    } catch (error) {
      logger.error('Failed to get access token for request:', error);
      // Return config without token - let request fail naturally
      return config;
    }
  },
  (error) => {
    return Promise.reject(error);
  },
);

/**
 * Response interceptor: Handle 401/403 errors with token refresh and retry
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401/403 and we haven't retried yet, attempt token refresh
    if (
      (error.response?.status === 401 || error.response?.status === 403) &&
      !originalRequest._retry &&
      getAccessTokenSilently
    ) {
      originalRequest._retry = true;

      try {
        // Prevent multiple simultaneous refresh attempts
        if (!isRefreshingToken) {
          isRefreshingToken = true;
          tokenPromise = getAccessTokenSilently();
        }

        // Wait for fresh token (tokenPromise is guaranteed to exist here)
        if (!tokenPromise) {
          throw new Error('Token refresh promise unexpectedly null');
        }

        const newToken = await tokenPromise;
        isRefreshingToken = false;
        tokenPromise = null;

        logger.debug('Token refreshed, retrying request');

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        isRefreshingToken = false;
        tokenPromise = null;
        logger.error('Token refresh failed:', refreshError);
        // Let the error propagate - might trigger logout in app
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export const handleApiResponse = <T>(response: AxiosResponse<T>): T => {
  return response.data;
};

export const getApiClient = (): AxiosInstance => {
  return apiClient;
};

export default apiClient;
